import type { ReactNode } from 'react';
import { createElement } from 'react';
import { Extension, type Editor, type Range } from '@tiptap/core';
import Suggestion, { type SuggestionOptions } from '@tiptap/suggestion';
import {
  BitbucketIcon,
  GitBranchIcon,
  GitHubIcon,
  GitLabIcon,
  LinearIcon,
  SentryIcon,
} from '../../../components/icons/ProviderIcons';

export type SlashCommandItem = {
  id: string;
  title: string;
  description: string;
  icon: ReactNode;
  searchTerms: string[];
};

const svgIcon = (path: string, color: string) =>
  createElement(
    'span',
    { className: `flex h-5 w-5 items-center justify-center rounded ${color}` },
    createElement(
      'svg',
      {
        xmlns: 'http://www.w3.org/2000/svg',
        className: 'h-3.5 w-3.5',
        fill: 'none',
        viewBox: '0 0 24 24',
        stroke: 'currentColor',
      },
      createElement('path', { strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '2', d: path }),
    ),
  );

export type SlashCommandRenderState = {
  editor: Editor;
  range: Range;
  query: string;
  items: SlashCommandItem[];
  clientRect: (() => DOMRect | null) | null;
};

export const slashCommands: SlashCommandItem[] = [
  {
    id: 'convention',
    title: 'Convention',
    description: 'Link a convention',
    icon: svgIcon(
      'M12 6.253v13m0-13C10.832 5.483 9.246 5 7.5 5 4.462 5 2 6.567 2 8.5v10C2 16.567 4.462 15 7.5 15c1.746 0 3.332.483 4.5 1.253m0-10C13.168 5.483 14.754 5 16.5 5c3.038 0 5.5 1.567 5.5 3.5v10c0-1.933-2.462-3.5-5.5-3.5-1.746 0-3.332.483-4.5 1.253',
      'text-info bg-info/10',
    ),
    searchTerms: ['convention', 'rule', '\uCEE8\uBCA4\uC158'],
  },
  {
    id: 'coaction',
    title: 'Co-Action',
    description: 'Link a co-action',
    icon: svgIcon(
      'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
      'text-secondary bg-secondary/10',
    ),
    searchTerms: ['coaction', 'co-action', '\uCF54\uC561\uC158'],
  },
  {
    id: 'plan',
    title: 'Plan',
    description: 'Link a plan',
    icon: svgIcon(
      'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
      'text-primary bg-primary/10',
    ),
    searchTerms: ['plan', '\uD50C\uB79C'],
  },
  {
    id: 'report',
    title: 'Completion Report',
    description: 'Link a completion report',
    icon: svgIcon(
      'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
      'text-accent bg-accent/10',
    ),
    searchTerms: ['report', '\uC644\uB8CC\uBCF4\uACE0\uC11C', '\uBCF4\uACE0\uC11C'],
  },
  {
    id: 'postmortem',
    title: 'Post Mortem',
    description: 'Link a post mortem',
    icon: svgIcon(
      'M9 17v-2m3 2v-4m3 4V7m2 12H7a2 2 0 01-2-2V5a2 2 0 012-2h7.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V17a2 2 0 01-2 2z',
      'text-warning bg-warning/10',
    ),
    searchTerms: ['postmortem', '\uC0AC\uD6C4\uBD84\uC11D'],
  },
  {
    id: 'codereview',
    title: 'Code Review',
    description: 'Link a code review',
    icon: svgIcon(
      'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z',
      'text-success bg-success/10',
    ),
    searchTerms: ['code', 'review', 'code review', 'code-review', 'codereview', '\uCF54\uB4DC\uB9AC\uBDF0'],
  },
  {
    id: 'document',
    title: 'Document',
    description: 'Link a document',
    icon: svgIcon(
      'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5A3.375 3.375 0 0010.125 2.25H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z',
      'text-neutral bg-neutral/10',
    ),
    searchTerms: ['document', 'doc', '\uBB38\uC11C', '\uBB38\uC11C\uD568'],
  },
  {
    id: 'skill',
    title: 'Skill',
    description: 'Link a skill',
    icon: svgIcon('M13 10V3L4 14h7v7l9-11h-7z', 'text-secondary bg-secondary/10'),
    searchTerms: ['skill', '스킬'],
  },
  {
    id: 'branch',
    title: 'Branch',
    description: 'Link a repository branch',
    icon: createElement(
      'span',
      { className: 'flex h-5 w-5 items-center justify-center rounded text-primary bg-primary/10' },
      createElement(GitBranchIcon, { className: 'h-3.5 w-3.5' }),
    ),
    searchTerms: ['branch', 'git', '브랜치'],
  },
  {
    id: 'github',
    title: 'GitHub',
    description: 'Link a GitHub issue or pull request',
    icon: createElement(
      'span',
      { className: 'flex h-5 w-5 items-center justify-center rounded text-success bg-success/10' },
      createElement(GitHubIcon, { className: 'h-3.5 w-3.5' }),
    ),
    searchTerms: ['github', 'issue', 'pr', 'pull request'],
  },
  {
    id: 'gitlab',
    title: 'GitLab',
    description: 'Link a GitLab issue or merge request',
    icon: createElement(
      'span',
      { className: 'flex h-5 w-5 items-center justify-center rounded text-warning bg-warning/10' },
      createElement(GitLabIcon, { className: 'h-3.5 w-3.5' }),
    ),
    searchTerms: ['gitlab', 'issue', 'mr', 'merge request'],
  },
  {
    id: 'bitbucket',
    title: 'Bitbucket',
    description: 'Link a Bitbucket issue or pull request',
    icon: createElement(
      'span',
      { className: 'flex h-5 w-5 items-center justify-center rounded text-info bg-info/10' },
      createElement(BitbucketIcon, { className: 'h-3.5 w-3.5' }),
    ),
    searchTerms: ['bitbucket', 'issue', 'pr', 'pull request'],
  },
  {
    id: 'linear',
    title: 'Linear',
    description: 'Link a Linear issue',
    icon: createElement(
      'span',
      { className: 'flex h-5 w-5 items-center justify-center rounded text-primary bg-primary/10' },
      createElement(LinearIcon, { className: 'h-3.5 w-3.5' }),
    ),
    searchTerms: ['linear', 'issue', 'ticket'],
  },
  {
    id: 'sentry',
    title: 'Sentry',
    description: 'Link a Sentry issue',
    icon: createElement(
      'span',
      { className: 'flex h-5 w-5 items-center justify-center rounded text-error bg-error/10' },
      createElement(SentryIcon, { className: 'h-3.5 w-3.5' }),
    ),
    searchTerms: ['sentry', 'issue', 'error'],
  },
];

type SlashCommandOptions = {
  suggestion: Omit<SuggestionOptions<SlashCommandItem>, 'editor'>;
  allowedCommands?: string[];
  onOpen?: (state: SlashCommandRenderState) => void;
  onUpdate?: (state: SlashCommandRenderState) => void;
  onClose?: () => void;
  onKeyDown?: (event: KeyboardEvent) => boolean;
};

export const filterSlashCommands = (query: string): SlashCommandItem[] => {
  const lowerQuery = query.toLowerCase();
  return slashCommands.filter((cmd) => {
    if (lowerQuery.length === 0) return true;
    return (
      cmd.title.toLowerCase().includes(lowerQuery) ||
      cmd.description.toLowerCase().includes(lowerQuery) ||
      cmd.searchTerms.some((term) => term.toLowerCase().includes(lowerQuery))
    );
  });
};

export const SlashCommand = Extension.create<SlashCommandOptions>({
  name: 'slashCommand',

  addOptions() {
    return {
      suggestion: {
        char: '/',
        allowSpaces: false,
        startOfLine: false,

        items: ({ query }: { query: string }) => filterSlashCommands(query),

        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).run();
        },
      },
    };
  },

  addProseMirrorPlugins() {
    const allowed = this.options.allowedCommands;
    const originalItems = this.options.suggestion.items;
    const filteredItems =
      originalItems && allowed
        ? (args: { query: string; editor: import('@tiptap/core').Editor }) => {
            const results = originalItems(args);
            if (Array.isArray(results)) {
              return results.filter((cmd) => allowed.includes(cmd.id));
            }
            return results;
          }
        : undefined;
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
        ...(filteredItems ? { items: filteredItems } : {}),
        render: () => ({
          onStart: (props) => {
            this.options.onOpen?.({
              editor: props.editor,
              range: props.range,
              query: props.query,
              items: props.items,
              clientRect: props.clientRect ?? null,
            });
          },
          onUpdate: (props) => {
            this.options.onUpdate?.({
              editor: props.editor,
              range: props.range,
              query: props.query,
              items: props.items,
              clientRect: props.clientRect ?? null,
            });
          },
          onKeyDown: (props) => this.options.onKeyDown?.(props.event) ?? false,
          onExit: () => {
            this.options.onClose?.();
          },
        }),
      }),
    ];
  },
});
